import { Dithering } from '@paper-design/shaders-react';
import { BookingCardBackProps } from './BookingCardBack';
import { Calendar, Clock, MapPin, Hash } from 'lucide-react';
import { formatDateLong, formatTime } from '@/utils/dateFormatting';

export function BookingCardSkyRippleBack({
  serviceName,
  appointmentDate,
  appointmentTime,
  locationLabel,
  publicConfirmationCode,
  theme,
}: BookingCardBackProps) {
  const formattedDate = formatDateLong(appointmentDate);
  const formattedTime = formatTime(appointmentTime);

  return (
    <div
      className="relative w-full h-full rounded-2xl overflow-hidden"
      style={{
        outline: `1px solid ${theme.borderColor}`,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.16), 0 12px 40px rgba(0, 0, 0, 0.24), 0 0 1px rgba(0, 0, 0, 0.2)',
      }}
    >
      {/* Animated Dithering Shader Background */}
      <Dithering
        speed={0.25}
        shape="ripple"
        type="random"
        size={4}
        scale={3.5}
        colorBack="#00000000"
        colorFront={theme.accentColor}
        className="absolute inset-0 w-full h-full"
        style={{ backgroundColor: theme.gradientFrom }}
      />

      {/* Content Layer */}
      <div className="relative z-10 w-full h-full p-8 flex flex-col">
        {/* Title */}
        <div
          className="font-bold mb-6"
          style={{
            color: theme.textPrimary,
            fontSize: 'clamp(1.125rem, 2.5vw + 0.5rem, 1.25rem)',
          }}
        >
          Booking Details
        </div>

        {/* Details */}
        <div className="flex-1 flex flex-col gap-4">
          {serviceName && (
            <div className="flex items-start gap-3">
              <div
                className="rounded-lg flex items-center justify-center flex-shrink-0"
                style={{
                  backgroundColor: theme.borderColor,
                  width: 'clamp(1.75rem, 3vw, 2rem)',
                  height: 'clamp(1.75rem, 3vw, 2rem)',
                }}
              >
                <Hash
                  style={{
                    color: theme.textPrimary,
                    width: 'clamp(0.875rem, 1.5vw, 1rem)',
                    height: 'clamp(0.875rem, 1.5vw, 1rem)',
                  }}
                />
              </div>
              <div className="flex flex-col">
                <div
                  className="uppercase tracking-wider mb-1"
                  style={{
                    color: theme.textSecondary,
                    fontSize: 'clamp(0.5625rem, 1.25vw, 0.625rem)',
                  }}
                >
                  SERVICE
                </div>
                <div
                  className="font-semibold"
                  style={{
                    color: theme.textPrimary,
                    fontSize: 'clamp(0.75rem, 1.75vw, 0.875rem)',
                  }}
                >
                  {serviceName}
                </div>
              </div>
            </div>
          )}

          <div className="flex items-start gap-3">
            <div
              className="rounded-lg flex items-center justify-center flex-shrink-0"
              style={{
                backgroundColor: theme.borderColor,
                width: 'clamp(1.75rem, 3vw, 2rem)',
                height: 'clamp(1.75rem, 3vw, 2rem)',
              }}
            >
              <Calendar
                style={{
                  color: theme.textPrimary,
                  width: 'clamp(0.875rem, 1.5vw, 1rem)',
                  height: 'clamp(0.875rem, 1.5vw, 1rem)',
                }}
              />
            </div>
            <div className="flex flex-col">
              <div
                className="uppercase tracking-wider mb-1"
                style={{
                  color: theme.textSecondary,
                  fontSize: 'clamp(0.5625rem, 1.25vw, 0.625rem)',
                }}
              >
                DATE
              </div>
              <div
                className="font-semibold"
                style={{
                  color: theme.textPrimary,
                  fontSize: 'clamp(0.75rem, 1.75vw, 0.875rem)',
                }}
              >
                {formattedDate}
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div
              className="rounded-lg flex items-center justify-center flex-shrink-0"
              style={{
                backgroundColor: theme.borderColor,
                width: 'clamp(1.75rem, 3vw, 2rem)',
                height: 'clamp(1.75rem, 3vw, 2rem)',
              }}
            >
              <Clock
                style={{
                  color: theme.textPrimary,
                  width: 'clamp(0.875rem, 1.5vw, 1rem)',
                  height: 'clamp(0.875rem, 1.5vw, 1rem)',
                }}
              />
            </div>
            <div className="flex flex-col">
              <div
                className="uppercase tracking-wider mb-1"
                style={{
                  color: theme.textSecondary,
                  fontSize: 'clamp(0.5625rem, 1.25vw, 0.625rem)',
                }}
              >
                TIME
              </div>
              <div
                className="font-semibold"
                style={{
                  color: theme.textPrimary,
                  fontSize: 'clamp(0.75rem, 1.75vw, 0.875rem)',
                }}
              >
                {formattedTime}
              </div>
            </div>
          </div>

          {locationLabel && (
            <div className="flex items-start gap-3">
              <div
                className="rounded-lg flex items-center justify-center flex-shrink-0"
                style={{
                  backgroundColor: theme.borderColor,
                  width: 'clamp(1.75rem, 3vw, 2rem)',
                  height: 'clamp(1.75rem, 3vw, 2rem)',
                }}
              >
                <MapPin
                  style={{
                    color: theme.textPrimary,
                    width: 'clamp(0.875rem, 1.5vw, 1rem)',
                    height: 'clamp(0.875rem, 1.5vw, 1rem)',
                  }}
                />
              </div>
              <div className="flex flex-col">
                <div
                  className="uppercase tracking-wider mb-1"
                  style={{
                    color: theme.textSecondary,
                    fontSize: 'clamp(0.5625rem, 1.25vw, 0.625rem)',
                  }}
                >
                  LOCATION
                </div>
                <div
                  className="font-semibold"
                  style={{
                    color: theme.textPrimary,
                    fontSize: 'clamp(0.75rem, 1.75vw, 0.875rem)',
                  }}
                >
                  {locationLabel}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Confirmation Code */}
        <div
          className="mt-6 pt-6"
          style={{
            borderTop: `1px solid ${theme.borderColor}`,
          }}
        >
          <div
            className="uppercase tracking-wider mb-2"
            style={{
              color: theme.textSecondary,
              fontSize: 'clamp(0.5625rem, 1.25vw, 0.625rem)',
            }}
          >
            CONFIRMATION CODE
          </div>
          <div
            className="font-mono font-bold"
            style={{
              color: theme.textPrimary,
              fontSize: 'clamp(0.875rem, 2vw, 1rem)',
            }}
          >
            {publicConfirmationCode}
          </div>
        </div>
      </div>
    </div>
  );
}
